<template>
  <main class="backup-page">
    <AppNavBar :title="t('wallet.walletBackup')" />

    <section class="account-card">
      <app-image src="@/assets/images/img_safe.png" alt="" />
      <div>
        <div class="account-card__title">
          <div data-strong>{{ accountName }}</div>
          <span v-if="isEmailWallet" class="account-card__tag">{{
            t("auth.emailLogin")
          }}</span>
        </div>
        <button type="button" @click="copyAddress">
          {{ shortAccountAddress }}
          <van-icon name="description-o" />
        </button>
      </div>
    </section>

    <van-cell-group v-if="showBackupOptions" inset class="backup-group">
      <van-cell
        v-if="hasMnemonic"
        :title="t('wallet.exportMnemonic')"
        :label="t('wallet.sensitiveExportLabel')"
        is-link
        @click="exportMnemonic"
      />
      <van-cell
        v-if="hasPrivateKey"
        :title="t('wallet.exportPrivateKey')"
        :label="t('wallet.sensitiveExportLabel')"
        is-link
        @click="confirmSensitive('privateKey')"
      />
    </van-cell-group>

    <section class="notice-card">
      <h2>{{ t("wallet.securityReminder") }}</h2>
      <p>{{ t("wallet.backupTip1") }}</p>
      <p>{{ t("wallet.backupTip2") }}</p>
      <p>{{ t("wallet.backupTip3") }}</p>
    </section>

    <PayPasswordPopup
      v-model:show="showPasswordDialog"
      v-model:password="passwordInput"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :confirm-disabled="!passwordInput.trim()"
      @closed="handlePasswordClosed"
      @confirm="confirmPrivateKeyPassword"
    />
  </main>
</template>

<script setup>
import { computed, ref } from "vue";
import { showConfirmDialog, showFailToast, showToast } from "vant";
import { showPassedToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import PayPasswordPopup from "@/components/PayPasswordPopup.vue";
import { useProfileStore } from "@/stores/profile";
import {
  DEFAULT_BRAND_NAME,
  resolveBrandName,
  useThemeStore,
} from "@/stores/theme";
import { copyToClipboard } from "@/utils/clipboard";
import { shortAddress } from "@/utils/format";
import { derivePrivateKeyByNetwork } from "@/utils/walletAddress";
import {
  getRecoverableMnemonic,
  getWalletPassword,
  getWalletSignerSecret,
  saveSensitiveExportPayload,
} from "@/utils/walletSession";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const themeStore = useThemeStore();
const showPasswordDialog = ref(false);
const passwordInput = ref("");

const accountName = computed(() =>
  resolveBrandName(
    profileStore.currentAccount.name,
    themeStore.name || DEFAULT_BRAND_NAME,
  ),
);
const accountAddress = computed(
  () =>
    profileStore.currentAddress || "0x0000000000000000000000000000000000000000",
);
const shortAccountAddress = computed(() => shortAddress(accountAddress.value));
const isEmailWallet = computed(
  () => String(profileStore.currentAccount?.walletWay || "") === "ByEmail",
);
const hasMnemonic = computed(() =>
  Boolean(getRecoverableMnemonic(profileStore.accountStorageAddress)),
);
const hasPrivateKey = computed(() =>
  Boolean(getWalletSignerSecret(profileStore.accountStorageAddress)?.secret),
);
const showBackupOptions = computed(
  () => hasMnemonic.value || hasPrivateKey.value,
);

async function copyAddress() {
  const copied = await copyToClipboard(accountAddress.value);
  if (copied) {
    showPassedToast(t("wallet.copySuccess"));
    return;
  }
  showFailToast(t("wallet.copyFailed"));
}

async function exportMnemonic() {
  try {
    await showConfirmDialog({
      title: t("wallet.exportMnemonic"),
      message: t("wallet.backupSensitiveConfirm"),
    });
    router.push("/auth/backup-warn");
  } catch (error) {
    // user cancelled
  }
}

async function loadCurrentPrivateKey() {
  const signer = getWalletSignerSecret(profileStore.accountStorageAddress);
  if (!signer?.secret) {
    return "";
  }
  return derivePrivateKeyByNetwork(
    signer.secret,
    signer.type,
    profileStore.currentNetwork,
  );
}

async function openPrivateKeyExport() {
  try {
    const privateKey = await loadCurrentPrivateKey();
    if (!privateKey) {
      showFailToast(t("wallet.privateKeyUnavailable"));
      return;
    }

    saveSensitiveExportPayload({
      type: "private-key",
      privateKey,
      networkName:
        profileStore.currentNetwork?.name ||
        profileStore.currentNetwork?.symbol ||
        "",
    });

    router.push("/wallet/backup/private-key");
  } catch (error) {
    showFailToast(error?.message || t("wallet.privateKeyUnavailable"));
  }
}

async function confirmSensitive(type) {
  try {
    await showConfirmDialog({
      title:
        type === "mnemonic"
          ? t("wallet.exportMnemonic")
          : t("wallet.exportPrivateKey"),
      message: t("wallet.backupSensitiveConfirm"),
    });

    if (type !== "privateKey") {
      return;
    }

    const savedPassword = getWalletPassword(profileStore.accountStorageAddress);
    if (!savedPassword) {
      await openPrivateKeyExport();
      return;
    }

    passwordInput.value = "";
    showPasswordDialog.value = true;
  } catch (error) {
    // user cancelled
  }
}

async function confirmPrivateKeyPassword() {
  const savedPassword = getWalletPassword(profileStore.accountStorageAddress);
  if (passwordInput.value !== savedPassword) {
    showFailToast(t("auth.passwordMismatch"));
    return;
  }

  showPasswordDialog.value = false;
  await openPrivateKeyExport();
}

function handlePasswordClosed() {
  passwordInput.value = "";
}
</script>

<style scoped lang="scss">
.backup-page {
  min-height: var(--app-viewport-height);
  padding: 15px 0 34px;
  background: #f7f7f7;
}

.account-card {
  display: flex;
  align-items: center;
  margin: 0 14px 12px;
  padding: 18px 16px;
  border-radius: 8px;
  background: #ffffff;

  .app-image {
    width: 52px;
    height: 52px;
    margin-right: 14px;
  }

  [data-strong] {
    display: block;
    color: var(--wp-color-text);
    font-size: 18px;
    font-weight: 700;
  }

  button {
    display: flex;
    align-items: center;
    margin-top: 8px;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--wp-color-sub-text);
    font-size: 13px;
  }

  .van-icon {
    margin-left: 6px;
  }
}

.account-card__title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.account-card__tag {
  padding: 3px 6px;
  border: 1px solid var(--wp-color-brand-4);
  border-radius: 4px;
  color: var(--wp-color-brand-1);
  font-size: 12px;
  line-height: 16px;
}

.backup-group {
  margin-bottom: 12px;
}

.notice-card {
  margin: 0 14px;
  padding: 18px 16px;
  border-radius: 8px;
  background: #ffffff;

  h2 {
    margin: 0 0 12px;
    color: var(--wp-color-text);
    font-size: 16px;
    font-weight: 500;
  }

  p {
    margin: 8px 0 0;
    color: #333333;
    font-size: 13px;
    line-height: 20px;
  }
}
</style>
